import React, { useState, useEffect } from 'react';
import { login, verify2FA, resend2FACode } from '../services/api';
import { useNavigate } from 'react-router-dom';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('admin_token');
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await login(email, password);
      if (response.success) {
        setLoginEmail(response.email);
        setShow2FA(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await verify2FA(loginEmail, twoFACode);
      if (response.success) {
        // Store token
        localStorage.setItem('admin_token', response.token);
        localStorage.setItem('admin_email', response.email);
        localStorage.setItem('token_expires_in', response.expires_in.toString());
        
        // Redirect to dashboard
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setResending(true);

    try {
      const response = await resend2FACode(loginEmail);
      if (response.success) {
        // Show success message
        setError('Verification code resent! Please check your email.');
        // Clear error after 5 seconds
        setTimeout(() => setError(null), 5000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const inputClass = "appearance-none relative block w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 sm:text-sm";
  const btnPrimary = "w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-black bg-orange-500 hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed";

  if (show2FA) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
              Two-Factor Authentication
            </h2>
            <p className="mt-2 text-center text-sm text-gray-400">
              We've sent a verification code to <strong className="text-orange-400">{loginEmail}</strong>
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleVerify2FA}>
            {error && (
              <div className={`border rounded-lg p-4 ${
                error.includes('resent') || error.includes('sent') 
                  ? 'bg-orange-900/30 border-orange-600' 
                  : 'bg-red-900/30 border-red-600'
              }`}>
                <p className={`text-sm ${
                  error.includes('resent') || error.includes('sent')
                    ? 'text-orange-200'
                    : 'text-red-200'
                }`}>{error}</p>
              </div>
            )}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-300 mb-2">
                Verification Code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                required
                maxLength="6"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                className={`${inputClass} text-center text-2xl tracking-widest`}
                placeholder="000000"
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500 text-center">
                Enter the 6-digit code sent to your email
              </p>
            </div>

            <div>
              <button type="submit" disabled={loading || twoFACode.length !== 6} className={btnPrimary}>
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
            </div>

            <div className="flex justify-between items-center">
              <button type="button" onClick={() => { setShow2FA(false); setTwoFACode(''); setError(null); }}
                className="text-sm text-orange-400 hover:text-orange-300">
                Back to login
              </button>
              <button type="button" onClick={handleResendCode} disabled={resending}
                className="text-sm text-orange-400 hover:text-orange-300 disabled:text-gray-500 disabled:cursor-not-allowed">
                {resending ? 'Resending...' : 'Resend Code'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Admin Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            Sign in to access the admin dashboard
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          {error && (
            <div className="bg-red-900/30 border border-red-600 rounded-lg p-4">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}
          <div className="space-y-3">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputClass} rounded-t-md`}
              placeholder="Email address"
            />
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputClass} rounded-b-md`}
              placeholder="Password"
            />
          </div>
          <div>
            <button type="submit" disabled={loading} className={btnPrimary}>
              {loading ? 'Logging in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
